import React, { useState, useEffect } from 'react';
import { fetchBillingSummary, UserCredits, SubscriptionInfo as Subscription, CreditTransaction } from '../../utils/api';
import { GCP_BACKEND_URL, SUBSCRIPTIONS_ENABLED } from '../../types';
import { BillingPageProps } from './types';
import './styles.scss';

const BillingPage: React.FC<BillingPageProps> = ({ user }) => {
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelDate, setCancelDate] = useState<string | null>(null);
  const [periodExpired, setPeriodExpired] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (user) {
      loadBillingData();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBillingData = async () => {
    if (!user) return;

    try {
      // Load credits, subscription and recent transactions in one call
      const summary = await fetchBillingSummary(user.id);
      setCredits(summary.credits);
      setSubscription(summary.subscription);
      setTransactions((summary.transactions || []).slice(0, 10));

      // Load invoices from Stripe
      try {
        const invoiceResp = await fetch(
          `${GCP_BACKEND_URL}/api/billing/invoices?user_email=${encodeURIComponent(user.email || '')}`
        );
        if (invoiceResp.ok) {
          const invoiceData = await invoiceResp.json();
          setInvoices(invoiceData.invoices || []);
        }
      } catch (invErr) {
        console.error('Failed to load invoices:', invErr);
      }

    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!user) return;

    if (!SUBSCRIPTIONS_ENABLED) {
      showToast('New subscriptions are currently unavailable.', 'info');
      return;
    }

    try {
      // Call backend to create Stripe checkout session
      const formData = new FormData();
      formData.append('user_email', user.email || '');
      formData.append('user_id', user.id);

      const response = await fetch(`${GCP_BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail || 'Failed to create checkout session';

        // If user already has a subscription, offer to manage it instead
        if (response.status === 400 && detail.toLowerCase().includes('already have')) {
          showToast('You already have an active Pro subscription. Use "Manage Subscription" to update it.', 'info');
          return;
        }
        throw new Error(detail);
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Upgrade error:', err);
      showToast(`${(err as Error).message}`, 'error');
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;

    try {
      const formData = new FormData();
      formData.append('user_email', user.email || '');

      const response = await fetch(`${GCP_BACKEND_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to open billing portal');
      }

      const data = await response.json();
      window.location.href = data.portal_url;
    } catch (err) {
      console.error('Portal error:', err);
      showToast(`Failed to open billing portal: ${(err as Error).message}`, 'error');
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;

    setCancelLoading(true);
    try {
      const formData = new FormData();
      formData.append('user_email', user.email || '');

      const response = await fetch(`${GCP_BACKEND_URL}/api/billing/cancel-subscription`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }

      const data = await response.json();
      showToast(data.message, 'success');
      setShowCancelConfirm(false);
      if (data.cancel_date) {
        setCancelDate(data.cancel_date);
      }
      if (data.period_expired) {
        setPeriodExpired(true);
      }
      loadBillingData(); // Refresh data
    } catch (err) {
      console.error('Cancel error:', err);
      showToast(`Failed to cancel subscription: ${(err as Error).message}`, 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!user) return;

    try {
      const formData = new FormData();
      formData.append('user_email', user.email || '');

      const response = await fetch(`${GCP_BACKEND_URL}/api/billing/reactivate-subscription`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reactivate subscription');
      }

      const data = await response.json();
      showToast(data.message, 'success');
      loadBillingData(); // Refresh data
    } catch (err) {
      console.error('Reactivate error:', err);
      showToast(`Failed to reactivate subscription: ${(err as Error).message}`, 'error');
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-text">Loading billing information...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`billing-toast billing-toast-${toast.type}`} onClick={() => setToast(null)}>
          <span className="billing-toast-icon">
            {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span className="billing-toast-message">{toast.message}</span>
          <button className="billing-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="page-header">
        <h1>Billing & Credits</h1>
        <p>Manage your subscription and credit balance</p>
      </div>

      <div className="billing-grid">
        {/* Credit Balance Card */}
        <div className="billing-card">
          <div className="billing-card-header">
            <h3>Credit Balance</h3>
            <div className="credit-badge">
              {credits?.credits || 0} Credits
            </div>
          </div>
          <div className="billing-card-content">
            <div className="stat-row">
              <span className="stat-label">Total Used:</span>
              <span className="stat-value">{credits?.total_used || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Remaining:</span>
              <span className="stat-value stat-highlight">{credits?.credits || 0}</span>
            </div>
            <p className="billing-note">Each analysis uses 1 credit</p>
          </div>
        </div>

        {/* Subscription Card */}
        <div className="billing-card">
          <div className="billing-card-header">
            <h3>Current Plan</h3>
            <div className={`plan-badge plan-${subscription?.plan_type || 'free'}`}>
              {subscription?.plan_type?.toUpperCase() || 'FREE'}
            </div>
          </div>
          <div className="billing-card-content">
            <div className="stat-row">
              <span className="stat-label">Status:</span>
              <span className={`status-badge status-${subscription?.status || 'inactive'}`}>
                {subscription?.status || 'inactive'}
              </span>
            </div>
            {subscription?.plan_type === 'free' || !subscription?.stripe_customer_id ? (
              <>
                <p className="billing-description">
                  You're on the free plan with 20 initial credits.
                </p>
                {SUBSCRIPTIONS_ENABLED && (
                  <>
                    <button onClick={handleUpgrade} className="btn btn-primary btn-large">
                      Upgrade to Pro / Add Credit
                    </button>
                    <p className="billing-note">Get 100 credits per month</p>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="billing-description">
                  You have an active Pro subscription.
                </p>
                <div className="subscription-actions">
                  <button
                    onClick={handleManageSubscription}
                    className="btn btn-secondary"
                    style={{ marginBottom: '8px' }}
                  >
                    Manage Subscription
                  </button>
                  {subscription?.status === 'active' && !(subscription as any).cancel_at_period_end && !showCancelConfirm && (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="btn btn-cancel"
                    >
                      Cancel Subscription
                    </button>
                  )}
                </div>

                {/* Cancel Confirmation Dialog */}
                {showCancelConfirm && (
                  <div className="cancel-confirm-box">
                    <p className="cancel-confirm-title">⚠️ Cancel Subscription?</p>
                    <p className="cancel-confirm-text">
                      Your Pro access will continue until the end of your current billing period. 
                      Your remaining credits will still be available for use. 
                      After the period ends, you'll be switched to the Free plan.
                    </p>
                    <div className="cancel-confirm-actions">
                      <button
                        onClick={handleCancelSubscription}
                        className="btn btn-danger"
                        disabled={cancelLoading}
                      >
                        {cancelLoading ? 'Canceling...' : 'Yes, Cancel'}
                      </button>
                      <button
                        onClick={() => setShowCancelConfirm(false)}
                        className="btn btn-secondary"
                      >
                        Keep Subscription
                      </button>
                    </div>
                  </div>
                )}

                {/* Reactivate button if subscription is set to cancel */}
                {subscription?.status === 'active' && (subscription as any).cancel_at_period_end && (
                  <div className="reactivate-box">
                    <p className="reactivate-text">
                      ⏳ Your subscription is set to cancel
                      {(cancelDate || (subscription as any).current_period_end) 
                        ? ` on ${new Date(cancelDate || (subscription as any).current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                        : ' at end of billing period'}.
                    </p>
                    <p className="credits-persist-text">
                      ✅ Your remaining <strong>{credits?.credits || 0} credits</strong> are still available for use until then.
                    </p>
                    <button onClick={handleReactivateSubscription} className="btn btn-primary">
                      Reactivate Subscription
                    </button>
                  </div>
                )}

                {/* Show info when period has expired after cancel */}
                {periodExpired && (subscription as any)?.plan_type === 'free' && (
                  <div className="reactivate-box" style={{ borderColor: '#6b7280' }}>
                    <p className="reactivate-text">
                      Your Pro subscription has ended.
                    </p>
                    <p className="credits-persist-text">
                      ✅ Your remaining <strong>{credits?.credits || 0} credits</strong> are still available for use.
                    </p>
                    {SUBSCRIPTIONS_ENABLED && (
                      <button onClick={handleUpgrade} className="btn btn-primary">
                        Re-subscribe to Pro
                      </button>
                    )}
                  </div>
                )}

                <p className="billing-note">
                  Pro plan: $1/month · 100 credits · Cancel anytime
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="billing-section">
        <h2>Transaction History</h2>
        {transactions.length === 0 ? (
          <div className="empty-state">
            <p>No transactions yet</p>
          </div>
        ) : (
          <div className="transaction-list">
            {transactions.map((trans) => (
              <div key={trans.id} className="transaction-item">
                <div className="transaction-icon">
                  {trans.amount > 0 ? '+' : '-'}
                </div>
                <div className="transaction-details">
                  <div className="transaction-description">{trans.description}</div>
                  <div className="transaction-date">
                    {new Date(trans.created_at).toLocaleString()}
                  </div>
                </div>
                <div className={`transaction-amount ${trans.amount > 0 ? 'positive' : 'negative'}`}>
                  {trans.amount > 0 ? '+' : ''}{trans.amount}
                </div>
                <div className="transaction-balance">
                  Balance: {trans.balance_after}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="billing-section">
        <h2>Invoices</h2>
        {invoices.length === 0 ? (
          <div className="empty-state">
            <p>No invoices yet</p>
          </div>
        ) : (
          <div className="invoice-list">
            {invoices.map((inv) => (
              <div key={inv.id} className="invoice-item">
                <div className="invoice-icon">🧾</div>
                <div className="invoice-details">
                  <div className="invoice-description">
                    Pro Subscription
                  </div>
                  <div className="invoice-date">
                    {new Date(inv.created * 1000).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </div>
                </div>
                <div className={`invoice-amount ${inv.status === 'paid' ? 'paid' : 'unpaid'}`}>
                  {inv.currency} {inv.amount.toFixed(2)}
                </div>
                <div className={`invoice-status status-${inv.status}`}>
                  {inv.status}
                </div>
                <div className="invoice-actions">
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="invoice-link" title="View Invoice">
                      View
                    </a>
                  )}
                  {inv.invoice_pdf && (
                    <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="invoice-link" title="Download PDF">
                      PDF
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pricing Plans */}
      <div className="billing-section">
        <h2>Available Plans</h2>
        <div className="pricing-grid-simple">
          <div className="pricing-card-simple">
            <h3>Free</h3>
            <div className="price-simple">$0<span>/month</span></div>
            <ul>
              <li>20 credits on signup</li>
              <li>Basic satellite data</li>
              <li>Community sharing</li>
            </ul>
            {subscription?.plan_type === 'pro' ? (
              <button className="btn btn-secondary btn-large" disabled>
                Free Plan
              </button>
            ) : (
              <button className="btn btn-secondary btn-large" disabled>
                Current Plan
              </button>
            )}
          </div>

          <div className="pricing-card-simple featured-simple">
            <h3>Pro</h3>
            <div className="price-simple">$1<span>/month</span></div>
            <ul>
              <li>100 credits per month</li>
              <li>Advanced satellite data</li>
              <li>Priority processing</li>
              <li>API access</li>
            </ul>
            {subscription?.plan_type === 'pro' && subscription?.stripe_customer_id ? (
              <button onClick={handleManageSubscription} className="btn btn-primary btn-large">
                Manage Subscription
              </button>
            ) : SUBSCRIPTIONS_ENABLED ? (
              <button onClick={handleUpgrade} className="btn btn-primary btn-large">
                Upgrade Now
              </button>
            ) : (
              <button className="btn btn-secondary btn-large" disabled>
                Currently Unavailable
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
